"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function FeaturedPostsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="19304" className="container mx-auto px-4">
        <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-3">
          {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
              <div className="bg-slate-200/50 aspect-[4/3] w-full animate-pulse rounded-xl" />
              <div className="bg-slate-200/50 h-6 w-24 animate-pulse rounded-full" />
              <div className="bg-slate-200/50 h-8 w-3/4 animate-pulse rounded-md" />
              <div className="bg-slate-200/50 h-20 w-full animate-pulse rounded-md" />
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="container mx-auto px-4 text-center">
        <p className="text-muted-foreground mb-4 font-default">לא ניתן לטעון מאמרים כעת.</p>
        <Button onClick={refetch} variant="outline" className="font-default rounded-full border-ring text-ring hover:bg-ring hover:text-white">
          נסה שנית
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="container mx-auto px-4 text-center">
        <p className="text-muted-foreground font-default">לא נמצאו מאמרים בבלוג.</p>
      </div>;
  }
  return <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-3">
        {posts.map((post, i) => {
        const postLink = post.link && post.link !== "#" ? post.link : null;
        return <Card key={post.id} data-index={i} className="group border-none bg-card shadow-lg rounded-xl overflow-hidden flex flex-col h-full transition-all duration-300 hover:scale-[1.02]">
              <CardHeader className="p-0 overflow-hidden">
                {postLink ? <Link to={postLink} className="block w-full overflow-hidden">
                    <AspectRatio ratio={4 / 3}>
                      {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]" /> : <div className="bg-muted flex h-full w-full items-center justify-center text-muted-foreground font-default">
                          אין תמונה
                        </div>}
                    </AspectRatio>
                  </Link> : <div className="block w-full overflow-hidden">
                    <AspectRatio ratio={4 / 3}>
                      {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover" /> : <div className="bg-muted flex h-full w-full items-center justify-center text-muted-foreground font-default">
                          אין תמונה
                        </div>}
                    </AspectRatio>
                  </div>}
              </CardHeader>
              <CardContent className="flex flex-col gap-4 px-8 pt-8 pb-4 grow">
                <div className="flex flex-wrap gap-2">
                  {post.categories && post.categories.length > 0 ? <Badge variant="secondary" className="bg-ring text-white hover:bg-ring/90 rounded-full px-4 py-1 text-xs uppercase tracking-wider font-medium font-default">
                      {post.categories[0].name}
                    </Badge> : <Badge variant="secondary" className="bg-ring text-white hover:bg-ring/90 rounded-full px-4 py-1 text-xs uppercase tracking-wider font-medium font-default">
                      מאמר מקצועי
                    </Badge>}
                </div>

                <h3 className="font-default font-medium text-2xl text-foreground leading-tight">
                  {postLink ? <Link to={postLink} className="hover:text-ring transition-colors">
                      {post.title}
                    </Link> : <span className="text-foreground">{post.title}</span>}
                </h3>

                <div className="font-default text-[17px] text-muted-foreground leading-relaxed line-clamp-3" dangerouslySetInnerHTML={{
              __html: post.excerpt || ""
            }} />
              </CardContent>
              <CardFooter className="px-8 pb-8 pt-0 mt-auto">
                {postLink && <Link to={postLink} className="group/link inline-flex items-center gap-2 font-default text-base font-medium bg-ring text-white px-8 py-3 rounded-full transition-all hover:bg-ring/90 w-full justify-center">
                    <span className="transition-colors duration-300">
                      קרא עוד
                    </span>
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </Link>}
              </CardFooter>
            </Card>;
      })}
      </div>
    </div>;
}
export default function FeaturedBlogPosts() {
  const wp_query_params = {
    per_page: 3,
    ignore_sticky: true
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="מאמרים-מומלצים" className="bg-gradient-to-b from-white to-slate-50 py-[160px] text-foreground">
      <PostsProvider wp_query={{
      per_page: 3,
      ignore_sticky: true
    }}>
        <FeaturedPostsConsumer />
      </PostsProvider>
    </section>;
}